import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ProgressBarModule } from 'primeng/progressbar';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';

// Services and models
import { CampaignsService } from '@generated/services/campaigns.service';
import { AgentsService } from '@generated/services/agents.service';
import { LanguagesService } from '@generated/services/languages.service';
import { FilesService } from '@generated/services/files.service';
import { CampaignItemDto } from '@generated/models/campaign-item-dto';
import { CampaignDto } from '@generated/models/campaign-dto';
import { CampaignStatus } from '@generated/models/campaign-status';
import { LanguageLocaleDto } from '@generated/models/language-locale-dto';
import { AgentLanguageLocaleDto } from '@generated/models/agent-language-locale-dto';
import { SpreadsheetRow } from '@generated/models/spreadsheet-row';
import { SpreadsheetSheet } from '@generated/models/spreadsheet-sheet';
import { SpreadsheetCell } from '@generated/models/spreadsheet-cell';
import { CampaignRegistryProperties } from '@generated/models/campaign-registry-properties';
import { TelecomService } from '@generated/services/telecom.service';
import { NotificationService } from '@core/services/notification.service';
import { PropertiesEdit } from '@shared/components/properties-edit/properties-edit';
import { descriptorEntriesToDict, valueEntriesToDict, dictToValueEntries } from '@shared/utils/openapi-schema-utils';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { LiveListenButtonComponent } from '@shared/components/live-listen-button/live-listen-button.component';
import { PreventOverlayCloseOnScrollDirective } from '@shared/directives/prevent-overlay-close-on-scroll';
import { CAMPAIGN_ITEM_FIELDS } from '@core/assistant/assistant-catalog';
import { AssistantUiService } from '@core/assistant/assistant-ui.service';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantAnchorDirective } from '@shared/directives/assistant-anchor.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { Signalr } from '@core/services/signalr';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-campaign-items',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    SelectModule,
    ProgressSpinnerModule,
    TagModule,
    MenuModule,
    DrawerModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    SplitButtonModule,
    ProgressBarModule,
    AccordionModule,
    PropertiesEdit,
    DeleteConfirmationComponent,
    LiveListenButtonComponent,
    PreventOverlayCloseOnScrollDirective,
    RouterLink,
    TooltipModule,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    AssistantAnchorDirective
  ],
  templateUrl: './campaign-items.html',
  styleUrl: './campaign-items.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CampaignItems implements OnInit, OnDestroy {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = CAMPAIGN_ITEM_FIELDS;
  private assistantUi = inject(AssistantUiService);
  private destroyRef = inject(DestroyRef);
  private campaignsService = inject(CampaignsService);
  private agentsService = inject(AgentsService);
  private languagesService = inject(LanguagesService);
  private filesService = inject(FilesService);
  private telecomService = inject(TelecomService);
  private notificationService = inject(NotificationService);
  private signalrService = inject(Signalr);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  campaignId: string = '';
  campaign: CampaignDto | null = null;
  agentVariablesSchema: any = null;
  items: CampaignItemDto[] = [];
  isLoading = false;
  isLoadingCampaign = false;
  nextCursor: string | null = null;
  readonly ROWS_PER_PAGE = 20;

  // Language locale data
  allLanguageLocales: LanguageLocaleDto[] = [];
  agentLanguageLocales: AgentLanguageLocaleDto[] = [];
  localeOptions: { label: string; value: string }[] = [];
  localeMap = new Map<string, LanguageLocaleDto>();

  virtualScrollOptions = {
    lazy: true,
    onLazyLoad: this.onLazyLoad.bind(this),
    itemSize: 60,
    delay: 150,
    showLoader: false,
    loading: false
  };

  // Filters
  selectedStatus: CampaignStatus | null = null;

  // Filter options
  statusOptions: { label: string; value: CampaignStatus }[] = [
    { label: $localize`:@@campaigns.status.initial:Initial`, value: 'Initial' },
    { label: $localize`:@@campaigns.status.processing:Processing`, value: 'Processing' },
    { label: $localize`:@@conversations.status.completed:Completed`, value: 'Completed' }
  ];

  // SplitButton menu items
  addItemMenuItems: MenuItem[] = [];

  readonly curlyBraces = '{{ }}';
  readonly campaignItemsFallback = $localize`:@@campaignItems.fallback:Campaign Items`;
  readonly addItemLabel = $localize`:@@campaignItems.addItem:Add Item`;
  readonly downloadingLabel = $localize`:@@campaignItems.downloading:Downloading...`;
  readonly editItemTitle = $localize`:@@campaignItems.editItem:Edit Item`;
  readonly goToConversationLabel = $localize`:@@campaignItems.goToConversation:Go to conversation`;
  readonly failedLabel = $localize`:@@campaigns.status.failed:Failed`;
  readonly unknownStatusLabel = $localize`:@@conversations.status.unknown:Unknown`;

  getItemStatusLabel(status: CampaignStatus | null | undefined, failed?: boolean): string {
    if (failed) return this.failedLabel;
    if (!status) return this.unknownStatusLabel;
    return this.statusOptions.find(o => o.value === status)?.label ?? status;
  }

  readonly deleteTitle = $localize`:@@campaignItems.delete.title:Delete Campaign Item`;
  get deleteSubtitle(): string {
    const phone = this.itemToDelete?.phone_number || $localize`:@@campaignItems.thisItem:this item`;
    return $localize`:@@campaignItems.delete.subtitle:Are you sure you want to delete item ${phone}:phone:? This action cannot be undone.`;
  }

  // Upload & preview state
  isUploading = false;
  uploadProgress = 0;
  isDownloadingTemplate = false;
  isAssigningFile = false;

  // Column mapping state
  systemColumnOptions: { label: string; value: string }[] = [];
  private registryProps: CampaignRegistryProperties | null = null;
  columnMapping: { [columnIndex: number]: string | null } = {};
  isSavingMapping = false;
  filePreviewActivePanel: string[] = [];
  firstRowIsHeader = true;

  // Drawer state
  showItemDrawer = false;
  editingItem: Partial<CampaignItemDto> = {};
  editingItemIndex = -1;
  variableValues: { [key: string]: any } = {};
  useCustomFirstMessage = false;
  editingFirstMessage = '';
  sendDigits = '';

  // Test call state
  isTestingCall = false;
  testCallSid: string | null = null;
  testCallSessionId: string | null = null;

  // Delete confirmation state
  showDeleteDialog = false;
  isDeleting = false;
  itemToDelete: CampaignItemDto | null = null;

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.paramMap.get('id') || '';

    this.registerVariablesField();
    this.buildMenuItems();

    if (this.campaignId) {
      this.loadCampaignAndAgent();
    }
  }

  private loadCampaignAndAgent(): void {
    this.isLoadingCampaign = true;

    // Load language locales in parallel
    this.languagesService.apiLanguagesLanguageLocalesGet().subscribe({
      next: (result) => {
        this.allLanguageLocales = result.language_locales || [];
        this.allLanguageLocales.forEach(ll => {
          if (ll.id) this.localeMap.set(ll.id, ll);
        });
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load language locales:', error);
      }
    });

    // Load registry properties in parallel
    this.campaignsService.apiCampaignsRegistryPropertiesGet().subscribe({
      next: (props) => {
        this.buildSystemColumnOptions(props);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load registry properties:', error);
      }
    });

    this.campaignsService.apiCampaignsCampaignIdGet({
      campaignId: this.campaignId
    }).subscribe({
      next: (campaign) => {
        this.campaign = campaign;
        this.buildMenuItems();
        this.filePreviewActivePanel = this.isInitialStatus ? ['file-preview'] : [];
        this.initColumnMappingFromCampaign();

        if (this.campaign?.agent_id) {
          this.agentsService.apiAgentsIdGet({
            id: this.campaign.agent_id
          }).subscribe({
            next: (agent) => {
              this.agentVariablesSchema = agent.variables_open_api_schema || null;
              this.agentLanguageLocales = agent.language_locales || [];
              this.buildLocaleOptions();
              this.appendVariablesToSystemOptions();
              this.autoMapColumns();
              this.isLoadingCampaign = false;
              this.loadMoreItems();
              this.cdr.markForCheck();
            },
            error: (error) => {
              console.error('Failed to load agent:', error);
              this.notificationService.showError($localize`:@@agentTest.notify.loadFail:Failed to load agent`);
              this.isLoadingCampaign = false;
              this.cdr.markForCheck();
            }
          });
        } else {
          this.notificationService.showWarning($localize`:@@campaignItems.notify.noAgent:Campaign has no agent assigned`);
          this.isLoadingCampaign = false;
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Failed to load campaign:', error);
        this.notificationService.showError($localize`:@@campaignEdit.notify.loadFail:Failed to load campaign`);
        this.isLoadingCampaign = false;
        this.cdr.markForCheck();
      }
    });
  }

  private buildLocaleOptions(): void {
    this.localeOptions = this.agentLanguageLocales
      .filter(al => al.language_locale_id)
      .map(al => {
        const ll = this.localeMap.get(al.language_locale_id!);
        const flag = ll?.locale_flag || '';
        const code = ll?.full_code || al.language_locale_id || '';
        return {
          label: `${flag} ${code}`.trim(),
          value: al.language_locale_id!
        };
      });
  }

  getLocaleDisplay(languageLocaleId: string | null | undefined): string {
    if (!languageLocaleId) return '-';
    const ll = this.localeMap.get(languageLocaleId);
    if (!ll) return languageLocaleId;
    return `${ll.locale_flag || ''} ${ll.full_code || ''}`.trim();
  }

  // --- Excel Upload ---

  triggerFileUpload() {
    this.fileInput?.nativeElement?.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected
    input.value = '';

    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    if (!validTypes.includes(file.type)) {
      this.notificationService.showError($localize`:@@campaignItems.notify.invalidFile:Please upload an Excel file (.xls or .xlsx)`);
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.cdr.markForCheck();

    this.filesService.apiFilesGenerateUploadUrlPost({
      body: {
        file_type: 'OutboundExcel',
        file_name: file.name,
        content_type: file.type
      }
    }).subscribe({
      next: async (generateResult) => {
        if (!generateResult?.file_id || !generateResult?.upload_url) {
          this.notificationService.showError($localize`:@@campaignItems.notify.uploadUrlFail:Failed to generate upload URL`);
          this.isUploading = false;
          this.cdr.markForCheck();
          return;
        }

        try {
          const uploadResponse = await fetch(generateResult.upload_url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type }
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload file');
          }

          this.uploadProgress = 50;
          this.cdr.markForCheck();
          this.checkFileExists(generateResult.file_id, 0);
        } catch (error: any) {
          this.notificationService.showError($localize`:@@campaignItems.notify.uploadFail:Failed to upload file: ${error.message}:message:`);
          this.isUploading = false;
          this.uploadProgress = 0;
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        this.notificationService.showError('Failed to generate upload URL');
        this.isUploading = false;
        this.uploadProgress = 0;
        this.cdr.markForCheck();
      }
    });
  }

  private checkFileExists(fileId: string, attempts: number) {
    const maxAttempts = 10;

    if (attempts >= maxAttempts) {
      this.notificationService.showError($localize`:@@campaignItems.notify.verifyTimeout:File upload verification timeout`);
      this.isUploading = false;
      this.uploadProgress = 0;
      this.cdr.markForCheck();
      return;
    }

    setTimeout(() => {
      this.filesService.apiFilesFileIdExistsGet({ fileId }).subscribe({
        next: (checkResult) => {
          if (checkResult?.exists) {
            this.uploadProgress = 100;
            this.cdr.markForCheck();
            this.assignFileToCampaign(fileId);
          } else {
            this.uploadProgress = 50 + ((attempts + 1) / maxAttempts) * 50;
            this.cdr.markForCheck();
            this.checkFileExists(fileId, attempts + 1);
          }
        },
        error: (error) => {
          this.notificationService.showError($localize`:@@campaignItems.notify.verifyFail:Failed to verify file`);
          this.isUploading = false;
          this.uploadProgress = 0;
          this.cdr.markForCheck();
        }
      });
    }, 1000);
  }

  private assignFileToCampaign(fileId: string) {
    this.isAssigningFile = true;
    this.isUploading = false;
    this.cdr.markForCheck();

    this.campaignsService.apiCampaignsCampaignIdAssignFilePost({
      campaignId: this.campaignId,
      body: {
        campaign_id: this.campaignId,
        file_stored_id: fileId
      }
    }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@campaignItems.notify.fileLinked:File linked to campaign`);
        this.isAssigningFile = false;
        // Reload campaign to get file_preview
        this.reloadCampaign();
      },
      error: (error) => {
        console.error('Failed to assign file to campaign:', error);
        this.notificationService.showError($localize`:@@campaignItems.notify.linkFail:Failed to link file to campaign`);
        this.isAssigningFile = false;
        this.cdr.markForCheck();
      }
    });
  }

  private reloadCampaign() {
    this.campaignsService.apiCampaignsCampaignIdGet({
      campaignId: this.campaignId
    }).subscribe({
      next: (campaign) => {
        this.campaign = campaign;
        this.buildMenuItems();
        this.filePreviewActivePanel = this.isInitialStatus ? ['file-preview'] : [];
        this.initColumnMappingFromCampaign();
        this.autoMapColumns();
        // Reload items
        this.items = [];
        this.nextCursor = null;
        this.loadMoreItems();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to reload campaign:', error);
        this.cdr.markForCheck();
      }
    });
  }

  get filePreviewSheet(): SpreadsheetSheet | null {
    const preview = this.campaign?.file_preview;
    if (!preview?.sheets?.length) return null;
    return preview.sheets[0];
  }

  get columnDefs(): { columnIndex: number; header: string }[] {
    const sheet = this.filePreviewSheet;
    if (!sheet?.rows?.length) return [];

    // Collect all unique column indices from data rows
    const columnIndices = new Set<number>();
    for (const row of sheet.rows) {
      if (row.cells) {
        for (const cell of row.cells) {
          if (cell.column !== undefined) columnIndices.add(cell.column);
        }
      }
    }
    const sorted = Array.from(columnIndices).sort((a, b) => a - b);

    if (this.firstRowIsHeader && sheet.rows.length > 0) {
      const firstRow = sheet.rows[0];
      return sorted.map(colIdx => {
        const cell = firstRow?.cells?.find((c: SpreadsheetCell) => c.column === colIdx);
        return { columnIndex: colIdx, header: cell?.value || `# ${colIdx}` };
      });
    }

    return sorted.map(colIdx => ({ columnIndex: colIdx, header: `# ${colIdx}` }));
  }

  get dataRows() {
    const sheet = this.filePreviewSheet;
    if (!sheet?.rows?.length) return [];
    return this.firstRowIsHeader ? sheet.rows.slice(1) : sheet.rows;
  }

  getCellValue(row: SpreadsheetRow, columnIndex: number): string {
    return row.cells?.find((c: SpreadsheetCell) => c.column === columnIndex)?.value ?? '';
  }

  onFirstRowIsHeaderChange(): void {
    this.columnMapping = {};
    if (this.firstRowIsHeader) {
      this.tryAutoMapColumns();
    }
    this.cdr.markForCheck();
  }

  // --- Column Mapping ---

  private buildSystemColumnOptions(props: CampaignRegistryProperties): void {
    this.registryProps = props;
    this.rebuildColumnOptions();
  }

  /**
   * Пересобирает опции маппинга из ОБОИХ источников сразу.
   *
   * Системные свойства и схема агента грузятся двумя независимыми запросами,
   * причём цепочка агента длиннее на один вызов (кампания → агент). Раньше
   * каждый источник писал в список сам: buildSystemColumnOptions обнулял его
   * и заполнял системными, appendVariablesToSystemOptions дописывал
   * переменные. Обычно системные приходили первыми и всё работало, но стоило
   * им задержаться — и они стирали уже добавленные переменные, оставляя в
   * выпадашке одни системные поля.
   */
  private rebuildColumnOptions(): void {
    const options: { label: string; value: string }[] = [];

    const props = this.registryProps;
    if (props) {
      for (const key of Object.keys(props).filter(k => (props as any)[k] !== undefined)) {
        options.push({ label: (props as any)[key] || key, value: key });
      }
    }

    // properties — МАССИВ OpenApiPropertyEntry { name, descriptor }, а не
    // словарь. Раньше он приводился к { [key: string]: any } и имена брались
    // через Object.keys — для массива это индексы, поэтому в опции уходили
    // {{0}}, {{1}} вместо {{name}}, {{city}}.
    for (const entry of this.agentVariablesSchema?.properties ?? []) {
      if (!entry?.name) continue;
      options.push({ label: `{{${entry.name}}}`, value: `{{${entry.name}}}` });
    }

    this.systemColumnOptions = options;
  }

  private appendVariablesToSystemOptions(): void {
    this.rebuildColumnOptions();
  }

  private initColumnMappingFromCampaign(): void {
    this.firstRowIsHeader = this.campaign?.first_row_is_header ?? true;
    this.columnMapping = {};
    if (!this.campaign?.column_mapping) return;
    for (const item of this.campaign.column_mapping) {
      if (item.column_index !== undefined && item.field) {
        this.columnMapping[item.column_index] = item.field;
      }
    }
  }

  private autoMapColumns(): void {
    // Only auto-map if backend has no mapping at all (null)
    if (this.campaign?.column_mapping != null) return;
    this.tryAutoMapColumns();
  }

  private tryAutoMapColumns(): void {
    const cols = this.columnDefs;
    if (!cols.length || !this.systemColumnOptions.length) return;

    // Build lookups: lowercase label -> value, lowercase value -> value
    const labelToValue = new Map<string, string>();
    const keyToValue = new Map<string, string>();
    for (const opt of this.systemColumnOptions) {
      labelToValue.set(opt.label.toLowerCase(), opt.value);
      keyToValue.set(opt.value.toLowerCase(), opt.value);
    }

    // Collect already used values (from existing mapping)
    const usedValues = new Set<string>();
    for (const val of Object.values(this.columnMapping)) {
      if (val) usedValues.add(val);
    }

    for (const col of cols) {
      // Skip already mapped columns
      if (this.columnMapping[col.columnIndex]) continue;

      const header = col.header?.trim().toLowerCase();
      if (!header || header.startsWith('#')) continue;

      // Match by label first, then by key (value)
      const matchedValue = labelToValue.get(header) || keyToValue.get(header);
      if (matchedValue && !usedValues.has(matchedValue)) {
        this.columnMapping[col.columnIndex] = matchedValue;
        usedValues.add(matchedValue);
      }
    }

    // Phone is required — warn if not mapped
    if (!usedValues.has('phone')) {
      this.notificationService.showWarning($localize`:@@campaignItems.notify.phoneNotMapped:Phone number column was not auto-mapped. Please map it manually.`);
    }
  }

  getAvailableOptionsForColumn(columnIndex: number): { label: string; value: string }[] {
    const usedValues = new Set<string>();
    for (const [idx, value] of Object.entries(this.columnMapping)) {
      if (Number(idx) !== columnIndex && value) {
        usedValues.add(value);
      }
    }
    return this.systemColumnOptions.filter(opt => !usedValues.has(opt.value));
  }

  onColumnMappingChange(): void {
    this.cdr.markForCheck();
  }

  saveColumnMapping(): void {
    const mapping = Object.entries(this.columnMapping)
      .filter(([_, field]) => !!field)
      .map(([idx, field]) => ({ column_index: Number(idx), field: field! }));

    this.isSavingMapping = true;
    this.cdr.markForCheck();

    this.campaignsService.apiCampaignsCampaignIdMapColumnsPost({
      campaignId: this.campaignId,
      body: {
        campaign_id: this.campaignId,
        first_row_is_header: this.firstRowIsHeader,
        mapping
      }
    }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@campaignItems.notify.mappingSaved:Column mapping saved`);
        this.isSavingMapping = false;
        this.reloadCampaign();
      },
      error: (error) => {
        console.error('Failed to save column mapping:', error);
        this.notificationService.showError($localize`:@@campaignItems.notify.mappingFail:Failed to save column mapping`);
        this.isSavingMapping = false;
        this.cdr.markForCheck();
      }
    });
  }

  get hasMappingChanges(): boolean {
    if (this.firstRowIsHeader !== (this.campaign?.first_row_is_header ?? true)) {
      return true;
    }
    const current = Object.entries(this.columnMapping)
      .filter(([_, field]) => !!field)
      .map(([idx, field]) => ({ column_index: Number(idx), field: field! }))
      .sort((a, b) => a.column_index - b.column_index);
    const saved = (this.campaign?.column_mapping || [])
      .slice()
      .sort((a, b) => (a.column_index ?? 0) - (b.column_index ?? 0));
    return JSON.stringify(current) !== JSON.stringify(saved);
  }

  // --- Download Template ---

  downloadTemplate() {
    if (!this.campaign?.agent_id) {
      this.notificationService.showWarning($localize`:@@campaignItems.notify.noAgentTemplate:No agent assigned to this campaign`);
      return;
    }

    this.isDownloadingTemplate = true;
    this.cdr.markForCheck();

    this.agentsService.apiAgentsIdCampaignTemplateGet({
      id: this.campaign.agent_id
    }).subscribe({
      next: (result) => {
        if (result.url) {
          window.open(result.url, '_blank');
        } else {
          this.notificationService.showError($localize`:@@campaignItems.notify.templateUrlMissing:Template URL not available`);
        }
        this.isDownloadingTemplate = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to get template:', error);
        this.notificationService.showError($localize`:@@campaignItems.notify.templateFail:Failed to download template`);
        this.isDownloadingTemplate = false;
        this.cdr.markForCheck();
      }
    });
  }

  // --- Lazy Load / Items ---

  onLazyLoad(event: any): void {
    const last = event.last || 0;
    const currentLength = this.items.length;

    if (currentLength > 0 && last >= currentLength - 5 && this.nextCursor && !this.isLoading) {
      this.loadMoreItems();
    }
  }

  private loadMoreItems(): void {
    if (this.isLoading) return;
    if (this.items.length > 0 && !this.nextCursor) return;

    this.isLoading = true;

    this.campaignsService.apiCampaignsCampaignIdItemsGet({
      campaignId: this.campaignId,
      status: this.selectedStatus || undefined,
      cursor: this.nextCursor || undefined,
      limit: this.ROWS_PER_PAGE
    }).subscribe({
      next: (result) => {
        const isFirstLoad = this.items.length === 0;

        if (isFirstLoad) {
          this.items = [...(result.items || [])];
        } else {
          this.items.push(...(result.items || []));
        }

        this.nextCursor = result.next_cursor || null;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load campaign items:', error);
        this.notificationService.showError($localize`:@@campaignItems.notify.loadFail:Failed to load campaign items`);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onFilterChange(): void {
    this.items = [];
    this.nextCursor = null;
    this.loadMoreItems();
  }

  get isInitialStatus(): boolean {
    return this.campaign?.status === 'Initial';
  }

  private buildMenuItems(): void {
    this.addItemMenuItems = [
      {
        label: $localize`:@@campaignItems.uploadExcel:Upload Excel`,
        icon: 'pi pi-upload',
        disabled: !this.isInitialStatus,
        command: () => this.triggerFileUpload()
      },
      {
        label: $localize`:@@campaignItems.downloadTemplate:Download Template`,
        icon: 'pi pi-download',
        command: () => this.downloadTemplate()
      }
    ];
    this.cdr.markForCheck();
  }

  canAddOrEditItems(): boolean {
    return !this.isLoadingCampaign && !!this.campaign?.agent_id;
  }

  addNewItem() {
    if (!this.canAddOrEditItems()) {
      this.notificationService.showWarning($localize`:@@campaignItems.notify.createNoAgent:Cannot create item: agent not selected`);
      return;
    }

    this.editingItem = {
      phone_number: '',
      language_locale_id: null,
      first_message: null,
      variables: null
    };

    this.variableValues = {};
    this.useCustomFirstMessage = false;
    this.editingFirstMessage = '';
    this.sendDigits = '';
    this.editingItemIndex = -1;
    this.showItemDrawer = true;
    this.cdr.markForCheck();
  }

  editItem(item: CampaignItemDto, event: Event) {
    event.stopPropagation();

    if (!this.canAddOrEditItems()) {
      this.notificationService.showWarning($localize`:@@campaignItems.notify.editNoAgent:Cannot edit item: agent not selected`);
      return;
    }

    this.editingItem = JSON.parse(JSON.stringify(item));
    this.variableValues = valueEntriesToDict(item.variables);
    this.useCustomFirstMessage = !!item.first_message;
    this.editingFirstMessage = item.first_message || '';
    this.sendDigits = '';
    this.editingItemIndex = this.items.findIndex(i => i.id === item.id);
    this.showItemDrawer = true;
    this.cdr.markForCheck();
  }

  /**
   * Переменные правятся своим редактором, а не контролом формы, — объявляем
   * их агенту руками. Значение целиком: переменные осмысленны вместе, по
   * одной их не правят.
   */
  private registerVariablesField(): void {
    const off = this.assistantUi.registerField({
      id: 'campaignItem.variables',
      surface: 'campaignItem',
      localId: 'variables',
      label: $localize`:@@campaignItems.variables.label:Variables`,
      kind: 'json',
      read: () => this.variableValues,
      write: value => {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        this.onVariableValuesChange((parsed ?? {}) as { [key: string]: any });
      },
      available: () => this.showItemDrawer && !!this.agentVariablesSchema,
    });
    this.destroyRef.onDestroy(off);
  }

  onVariableValuesChange(values: { [key: string]: any }): void {
    this.variableValues = values;
  }

  getVariableSchemas(): Array<{ [key: string]: any }> {
    if (!this.agentVariablesSchema || !this.agentVariablesSchema.properties) {
      return [];
    }
    return [descriptorEntriesToDict(this.agentVariablesSchema.properties)];
  }

  saveItemEdit() {
    if (!this.editingItem || !this.canAddOrEditItems()) return;

    const variables = dictToValueEntries(this.variableValues);
    const firstMessage = this.useCustomFirstMessage ? this.editingFirstMessage : null;

    if (this.editingItemIndex === -1) {
      // Create new item
      this.campaignsService.apiCampaignsCampaignIdItemsPost({
        campaignId: this.campaignId,
        body: {
          campaign_id: this.campaignId,
          phone_number: this.editingItem.phone_number,
          language_locale_id: this.editingItem.language_locale_id,
          first_message: firstMessage,
          variables: variables
        }
      }).subscribe({
        next: () => {
          this.notificationService.showSuccess($localize`:@@campaignItems.notify.created:Item created`);
          this.items = [];
          this.nextCursor = null;
          this.loadMoreItems();
          this.closeItemDrawer();
        },
        error: (error) => {
          console.error('Failed to create item:', error);
          this.notificationService.showError($localize`:@@campaignItems.notify.createFail:Failed to create item`);
        }
      });
    } else {
      // Update existing item
      if (!this.editingItem.id) return;

      this.campaignsService.apiCampaignsItemsCampaignItemIdPut({
        campaignItemId: this.editingItem.id,
        body: {
          campaign_item_id: this.editingItem.id,
          phone_number: this.editingItem.phone_number,
          language_locale_id: this.editingItem.language_locale_id,
          first_message: firstMessage,
          variables: variables
        }
      }).subscribe({
        next: () => {
          this.notificationService.showSuccess($localize`:@@campaignItems.notify.updated:Item updated`);
          this.items = [];
          this.nextCursor = null;
          this.loadMoreItems();
          this.closeItemDrawer();
        },
        error: (error) => {
          console.error('Failed to update item:', error);
          this.notificationService.showError($localize`:@@campaignItems.notify.updateFail:Failed to update item`);
        }
      });
    }
  }

  testCall() {
    if (!this.editingItem.phone_number || !this.campaign?.agent_id) return;

    this.isTestingCall = true;
    this.testCallSid = null;
    this.testCallSessionId = null;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomOutboundCallPost({
      body: {
        agent_id: this.campaign.agent_id,
        phone_number_agent_id: this.campaign.phone_number_agent_id || undefined,
        to_phone_number: this.editingItem.phone_number,
        language_locale_id: this.editingItem.language_locale_id,
        variables: dictToValueEntries(this.variableValues),
        send_digits: this.sendDigits || undefined
      }
    }).subscribe({
      next: (result) => {
        if (result.success) {
          this.notificationService.showSuccess($localize`:@@campaignItems.notify.testCallStarted:Test call initiated`);
          if (result.call_sid) {
            this.testCallSid = result.call_sid;
            this.subscribeForLiveSession(result.call_sid);
          }
        } else {
          this.notificationService.showError(result.error || $localize`:@@campaignItems.notify.testCallFail:Failed to initiate test call`);
        }
        this.isTestingCall = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to initiate test call:', error);
        this.notificationService.showError($localize`:@@campaignItems.notify.testCallFail:Failed to initiate test call`);
        this.isTestingCall = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Watch for the AgentSession matching the just-issued call_sid so we
   * can light up the "Live" listen button. Resolves on first match.
   */
  private subscribeForLiveSession(callSid: string): void {
    const match$ = new Subject<void>();
    this.signalrService.agentSessionUpdated$
      .pipe(takeUntil(match$), takeUntil(this.destroy$))
      .subscribe((session) => {
        if (session.call_sid === callSid && session.id) {
          this.testCallSessionId = session.id;
          this.cdr.markForCheck();
          match$.next();
          match$.complete();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeItemDrawer() {
    this.showItemDrawer = false;
    this.editingItem = {};
    this.editingItemIndex = -1;
    this.variableValues = {};
    this.useCustomFirstMessage = false;
    this.editingFirstMessage = '';
    this.cdr.markForCheck();
  }

  deleteItem(item: CampaignItemDto, event: Event) {
    event.stopPropagation();
    if (!item.id) return;

    this.itemToDelete = item;
    this.showDeleteDialog = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirm() {
    if (!this.itemToDelete?.id) return;

    this.isDeleting = true;

    this.campaignsService.apiCampaignsItemsCampaignItemIdDelete({
      campaignItemId: this.itemToDelete.id
    }).subscribe({
      next: () => {
        this.items = this.items.filter(i => i.id !== this.itemToDelete!.id);
        this.notificationService.showSuccess($localize`:@@campaignItems.notify.deleted:Item deleted`);
        this.showDeleteDialog = false;
        this.isDeleting = false;
        this.itemToDelete = null;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to delete item:', error);
        this.notificationService.showError($localize`:@@campaignItems.notify.deleteFail:Failed to delete item`);
        this.isDeleting = false;
        this.cdr.markForCheck();
      }
    });
  }

  trackByItemId(index: number, item: CampaignItemDto): string | number {
    return item?.id || index;
  }

  getStatusSeverity(status: CampaignStatus | undefined): 'secondary' | 'info' | 'success' | 'warn' | 'danger' {
    switch (status) {
      case 'Initial':
        return 'secondary';
      case 'Processing':
        return 'info';
      case 'Completed':
        return 'success';
      default:
        return 'secondary';
    }
  }

  goBackToCampaign() {
    this.router.navigate(['/outbound-campaigns', this.campaignId]);
  }
}
